import React, { useEffect, useRef, useState } from "react";
import { useAmazonS3Connection } from "@via/hooks/useAmazonS3Connection.tsx";
import {
  prepareBucketPath,
  useAmazonS3FolderList,
} from "@via/pages/data/data-sources/data-source.api.tsx";
import {
  AwsFileUploadPath,
  DataConnectionCredentials,
  DataSourceProvider,
} from "common";
import {
  BucketObject,
  getParentDirectory,
} from "@via/pages/data/data-sources/utils/helpers.tsx";
import FileBrowserWrapper from "@via/pages/data/data-sources/cloud-data-sources/common/file-browser/file-browser-wrapper.tsx";
import { fileUpload } from "@via/pages/data/data-sources/cloud-data-sources/common/file-upload.ts";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import Modal from "@via/components/modal.tsx";
import { FILE_UPLOAD_DISCLAIMER, ModalSize } from "@via/utils/constants.tsx";
import FileUploader, {
  FileUploaderHandle,
} from "@via/components/file-uploader/file-uploader.tsx";
import {
  BannerSize,
  BannerType,
  MessageBanner,
} from "@via/components/message-banner/message-banner.tsx";

export interface PathForm {
  path: string;
}

export enum AdditionalArea {
  DataSourceList = "dataSourceList",
  SaveDataSource = "saveDataSource",
  Filters = "filters",
}

interface AmazonFolderBrowserProps {
  onContinue: any;
  cart?: BucketObject[];
  setCart?: (cart: BucketObject[]) => void;
  uploadModalOpen: boolean;
  setUploadModalOpen: (open: boolean) => void;
}

const AmazonFolderBrowser: React.FC<AmazonFolderBrowserProps> = ({
  onContinue,
  cart,
  setCart,
  uploadModalOpen,
  setUploadModalOpen,
}) => {
  const [progressPercentageList, setProgressPercentageList] = useState<
    { filename: string; progress: number }[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pathValue, setPathValue] = useState<string>("");
  const [cancelUploadFn, setCancelUploadFn] = useState<(() => void) | null>(
    null
  );
  const { setNotification } = useNotifications();

  const { connectionCredentials, setConnectionCredentials } =
    useAmazonS3Connection();

  const [folderList, setFolderList] = useState<any>();

  const {
    mutateAsync: getFolderList,
    data,
    isPending: isGetFolderListLoading,
    isSuccess: isGetFolderListSuccess,
  } = useAmazonS3FolderList();

  useEffect(() => {
    if (isGetFolderListSuccess && data) {
      setFolderList(data);
    }
  }, [isGetFolderListSuccess, data]);

  const {
    mutateAsync: getExtraFolderList,
    isPending: isGetExtraFolderListLoading,
  } = useAmazonS3FolderList();

  useEffect(() => {
    if (connectionCredentials) {
      getFolderList(connectionCredentials);
    }
  }, [connectionCredentials]);

  const handlePathClick = (payload: PathForm) => {
    if (!payload.path) {
      return;
    }

    const requestParams = Object.assign({}, connectionCredentials);
    requestParams.prefix = undefined;
    requestParams.bucketName = payload.path;
    prepareBucketPath(requestParams);

    setConnectionCredentials(requestParams);
  };

  const handleObjectClick = (object: BucketObject) => {
    const requestParams = Object.assign({}, connectionCredentials);
    if (object.id === "..") {
      requestParams.prefix = getParentDirectory(requestParams.prefix);
    } else {
      requestParams.prefix = object.name ? object.name : undefined;
    }

    setConnectionCredentials(requestParams);
  };

  const handleExtraFolderObjects = async (item: any) => {
    const requestParams = Object.assign({}, connectionCredentials);
    requestParams.prefix = item.name;
    requestParams.recursive = true;
    const extraFolderList = await getExtraFolderList(requestParams);
    return extraFolderList || {};
  };

  const handleFileUpload = (files: File[]) => {
    const uploadCredentials = {
      ...connectionCredentials,
    };
    uploadCredentials.bucketName = uploadCredentials.bucketName
      ? uploadCredentials.bucketName
      : "";

    const cancelFunctions = fileUpload(
      AwsFileUploadPath,
      files,
      uploadCredentials as DataConnectionCredentials,
      setNotification,
      handleFileUploadSuccess,
      setIsLoading,
      setProgressPercentageList
    );
    setCancelUploadFn(() => cancelFunctions?.cancelAll);
  };

  const handleFileUploadSuccess = async () => {
    setProgressPercentageList([]);
    setUploadModalOpen(false);
    setCancelUploadFn(null);
    const requestParams = Object.assign({}, connectionCredentials);
    await getFolderList(requestParams);
  };

  const fileUploaderRef = useRef<FileUploaderHandle>(null);

  const handleModalClose = async (open: boolean) => {
    if (!open) {
      // Check with FileUploader if close should be prevented
      if (fileUploaderRef.current) {
        const canClose = await fileUploaderRef.current.checkBeforeClose();
        if (!canClose) {
          return; // Don't close - FileUploader will show confirmation
        }
      } else if (isLoading && cancelUploadFn) {
        // Fallback if ref not available
        return; // Don't close
      }
    }
    setUploadModalOpen(open);
  };

  const handleBeforeClose = async (): Promise<boolean> => {
    // Delegate to FileUploader to handle confirmation
    if (fileUploaderRef.current) {
      return await fileUploaderRef.current.checkBeforeClose();
    }
    // If no ref, check directly
    if (isLoading && cancelUploadFn) {
      return false; // Prevent closing - FileUploader will show confirmation
    }
    return true; // Allow closing
  };

  const handleConfirmTerminate = () => {
    // User confirmed termination - close modal
    setUploadModalOpen(false);
    setProgressPercentageList([]);
    setCancelUploadFn(null);
  };

  return (
    <>
      <FileBrowserWrapper
        connectionCredentials={
          connectionCredentials as DataConnectionCredentials
        }
        folderList={folderList}
        dataSourceProvider={DataSourceProvider.Amazon}
        onObjectClick={handleObjectClick}
        onPathChange={handlePathClick}
        getExtraFolderObjects={handleExtraFolderObjects}
        onContinue={onContinue}
        isGetFolderListLoading={isGetFolderListLoading}
        isGetExtraFolderListLoading={isGetExtraFolderListLoading}
        cart={cart}
        setCart={setCart}
        onPathValueChange={setPathValue}
      />

      <Modal
        open={uploadModalOpen}
        setOpen={handleModalClose}
        size={ModalSize.Large}
        showButtons={false}
        title="Upload Files to AWS"
        onBeforeClose={handleBeforeClose}>
        <div className="min-h-100 max-w-250 flex w-full flex-col items-center">
          <div className="mb-4">
            <MessageBanner
              messages={FILE_UPLOAD_DISCLAIMER}
              type={BannerType.Warning}
              size={BannerSize.Small}
            />
          </div>

          <FileUploader
            ref={fileUploaderRef}
            onUpload={handleFileUpload}
            onCancel={cancelUploadFn || undefined}
            onConfirmTerminate={handleConfirmTerminate}
            maxFiles={100}
            isLoading={isLoading}
            progressPercentageList={progressPercentageList}
            prefix={connectionCredentials?.prefix}
            showFolderCreation
            fullDestinationPath={pathValue}
          />
        </div>
      </Modal>
    </>
  );
};

export default AmazonFolderBrowser;
